import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Plus, X, Type, StickyNote, Image as ImageIcon, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { logActivity } from "@/lib/activity-logger";
import { uploadAndGetSignedUrl, refreshSignedUrls, validateFileSize } from "@/lib/storage";

export default function VisionBoardPage() {
  const { user, profile } = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [newContent, setNewContent] = useState("");
  const [newType, setNewType] = useState<"text" | "note">("text");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const workspaceId = profile?.workspace_id;

  useEffect(() => {
    if (!workspaceId) return;
    supabase
      .from("vision_items")
      .select("*")
      .eq("workspace_id", workspaceId)
      .order("created_at", { ascending: false })
      .then(async ({ data, error }) => {
        if (error) toast.error("Couldn't load vision board", { description: error.message });
        const items = data || [];
        const withUrls = await refreshSignedUrls(items, "image_url");
        setItems(withUrls);
        setLoading(false);
      });
  }, [workspaceId]);

  const addItem = async () => {
    if (!newContent.trim() || !workspaceId) return;
    try {
      const colors = ["bg-sage-light", "bg-terracotta-light", "bg-card"];
      const color = colors[Math.floor(Math.random() * colors.length)];
      const { data, error } = await supabase
        .from("vision_items")
        .insert({ workspace_id: workspaceId, created_by: user!.id, type: newType, content: newContent.trim(), color })
        .select()
        .single();
      if (error) { toast.error("Couldn't add", { description: error.message }); return; }
      setItems([data, ...items]);
      setNewContent("");
      setIsAdding(false);
      logActivity("vision_item_added", { type: newType });

      // Notify partner
      const displayName = profile?.preferred_name || profile?.full_name || "Your partner";
      supabase.functions.invoke("send-notification-email", {
        body: { type: "vision_update", workspaceId, actorName: displayName },
      }).catch(console.error);
    } catch (e: any) {
      toast.error("Couldn't add", { description: e.message });
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !workspaceId) return;
    const sizeErr = validateFileSize(file);
    if (sizeErr) { toast.error(sizeErr); return; }
    setUploading(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `${workspaceId}/vision-${Date.now()}.${ext}`;
      const { signedUrl, error: uploadError } = await uploadAndGetSignedUrl("couple-uploads", path, file);
      if (uploadError || !signedUrl) { toast.error("Upload failed", { description: uploadError || "Unknown error" }); setUploading(false); return; }
      const { data, error } = await supabase
        .from("vision_items")
        .insert({ workspace_id: workspaceId, created_by: user!.id, type: "image", content: file.name, image_url: path })
        .select()
        .single();
      if (error) { toast.error("Couldn't save", { description: error.message }); }
      else {
        setItems([{ ...data, image_url: signedUrl }, ...items]);
        logActivity("vision_item_added", { type: "image" });
      }
    } catch (e: any) {
      toast.error("Upload failed", { description: e.message });
    }
    setUploading(false);
  };

  const removeItem = async (id: string) => {
    await supabase.from("vision_items").delete().eq("id", id);
    setItems(items.filter((i) => i.id !== id));
  };

  return (
    <div className="space-y-8 max-w-full">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <h1 className="text-3xl md:text-4xl font-display font-semibold text-foreground">Our Vision</h1>
        <p className="mt-2 text-muted-foreground text-lg">A living canvas of what we're building toward, together.</p>
      </motion.div>

      {isAdding ? (
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="bg-card border border-border rounded-xl p-5 space-y-4">
          <div className="flex gap-2">
            <button onClick={() => setNewType("text")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${newType === "text" ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}>
              <Type className="w-3.5 h-3.5" /> Affirmation
            </button>
            <button onClick={() => setNewType("note")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${newType === "note" ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}>
              <StickyNote className="w-3.5 h-3.5" /> Note
            </button>
          </div>
          <textarea value={newContent} onChange={(e) => setNewContent(e.target.value)} placeholder={newType === "text" ? "Write an affirmation..." : "Write a note..."} className="w-full bg-background border border-input rounded-lg p-3 text-sm resize-none h-24 focus:outline-none focus:ring-2 focus:ring-ring" />
          <div className="flex gap-2">
            <button onClick={addItem} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90">Add to Board</button>
            <button onClick={() => { setIsAdding(false); setNewContent(""); }} className="px-4 py-2 bg-secondary text-secondary-foreground rounded-lg text-sm font-medium">Cancel</button>
          </div>
        </motion.div>
      ) : (
        <div className="flex gap-2">
          <button onClick={() => setIsAdding(true)} className="flex items-center gap-2 px-4 py-2.5 bg-secondary text-secondary-foreground rounded-lg text-sm font-medium hover:bg-secondary/80 transition-colors">
            <Plus className="w-4 h-4" /> Add Text
          </button>
          <label className="flex items-center gap-2 px-4 py-2.5 bg-secondary text-secondary-foreground rounded-lg text-sm font-medium hover:bg-secondary/80 transition-colors cursor-pointer">
            <ImageIcon className="w-4 h-4" /> {uploading ? "Uploading..." : "Add Image"}
            <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
          </label>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading vision board...</span>
        </div>
      ) : (
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
          {items.map((item, i) => (
            <motion.div key={item.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }} className={`${item.color || "bg-card"} border border-border rounded-xl p-5 break-inside-avoid group relative`}>
              <button onClick={() => removeItem(item.id)} className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-full hover:bg-secondary">
                <X className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
              {item.type === "image" && item.image_url ? (
                <img src={item.image_url} alt={item.content} className="w-full rounded-lg max-w-full" loading="lazy" />
              ) : item.type === "text" ? (
                <p className="font-display text-lg font-medium text-foreground">{item.content}</p>
              ) : (
                <p className="text-sm text-foreground/80 italic leading-relaxed">{item.content}</p>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
